/**
 * usePortal — React Query hooks for the admin portal.
 * Pages stay declarative; session writes and cache invalidation happen here.
 */

import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AxiosError } from "axios";

import * as portalApi from "../api/portalApi";
import { useAuthStore } from "../store/authStore";
import { usePortalAuthStore } from "../store/portalAuthStore";
import { LoginFormType } from "../types/login";
import {
  DashboardCreatePayload,
  DashboardExcludedClientsPayload,
  DashboardTiersAssignPayload,
  DashboardUpdatePayload,
  PortalDashboard,
  PortalProgram,
  PortalTier,
  PortalUser,
  PortalWidgetTemplate,
  ProductCreatePayload,
  ProductUpdatePayload,
  ProgramCreatePayload,
  ProgramUpdatePayload,
  TierCreatePayload,
  TierUpdatePayload,
  UserCreatePayload,
  UserProfileUpdatePayload,
  WidgetTemplateCreatePayload,
  WidgetTemplateUpdatePayload,
  WidgetTemplateUpdateResponse,
} from "../types/portal";

export const PORTAL_KEYS = {
  me: ["portal", "me"] as const,
  users: ["portal", "users"] as const,
  roles: ["portal", "roles"] as const,
  programs: ["portal", "programs"] as const,
  programTiers: (id: number) => ["portal", "programs", id, "tiers"] as const,
  products: ["portal", "products"] as const,
  tiers: ["portal", "tiers"] as const,
  dashboards: ["portal", "dashboards"] as const,
  widgetTemplates: ["portal", "widgetTemplates"] as const,
} as const;

/**
 * FastAPI's `detail` is a string on domain errors (404/409), an array of
 * validation objects on 422, or a plain object with its own `message` (the
 * 429 cooldown on resend-invite) — flatten any of those into a toast message.
 */
function toastApiError(fallback: string) {
  return (error: unknown) => {
    const detail = (error as AxiosError<{ detail?: unknown }>)?.response?.data
      ?.detail;
    if (typeof detail === "string") return toast.error(detail);
    if (Array.isArray(detail)) {
      const messages = detail
        .map((item) => (item as { msg?: string })?.msg)
        .filter(Boolean)
        .join(", ");
      return toast.error(messages || fallback);
    }
    if (detail && typeof detail === "object" && "message" in detail) {
      const message = (detail as { message?: unknown }).message;
      if (typeof message === "string") return toast.error(message);
    }
    toast.error(fallback);
  };
}

/** Login → store the session → land on the users page. */
export function usePortalLogin() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { startSession } = usePortalAuthStore();

  return useMutation({
    mutationFn: (credentials: LoginFormType) => portalApi.portalLogin(credentials),
    onSuccess: (data) => {
      // One session for both the portal and the analytics app (confirmed
      // live: /admin/auth/login returns the same token pair as /auth/login,
      // accepted by both /me endpoints). Saving it through the main store
      // writes the shared cookies, so "open in builder" doesn't hit the
      // analytics login wall, and both sides renew through the same refresh
      // token — which is single-use, so there must be only this one copy.
      useAuthStore.getState().loginSuccess({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresIn: data.expires_in,
        refreshTokenExpiresIn: data.refresh_token_expires_in,
      });
      startSession();

      // Drop any identity cached from a previous session before the gate re-checks.
      queryClient.removeQueries({ queryKey: PORTAL_KEYS.me });
      navigate("/portal/users", { replace: true });
    },
  });
}

/** Identity + role for the portal gate. */
export function usePortalMe(enabled = true) {
  return useQuery({
    queryKey: PORTAL_KEYS.me,
    queryFn: portalApi.getPortalMe,
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/** staleTime 0, not the app-wide 60 minutes (App.tsx): this is an admin list
 *  that several admins edit at once, and a row's role, status or product
 *  assignment can change from another session. Always refetch on mount and
 *  on tab refocus; the cached rows still render instantly while it does. */
export const usePortalUsers = () =>
  useQuery({
    queryKey: PORTAL_KEYS.users,
    queryFn: portalApi.listPortalUsers,
    staleTime: 0,
  });

export const usePortalRoles = () =>
  useQuery({
    queryKey: PORTAL_KEYS.roles,
    queryFn: portalApi.listPortalRoles,
    staleTime: Infinity,
  });

// ── Users ─────────────────────────────────────────────────────────────────────

/** Insert or replace one row in the cached users list — avoids refetching
 *  GET /admin/users (the whole list) after a single-user mutation. */
function putUserInCache(queryClient: QueryClient, user: PortalUser) {
  queryClient.setQueryData<PortalUser[]>(PORTAL_KEYS.users, (old) => {
    if (!old) return old;
    const exists = old.some((existing) => existing.id === user.id);
    return exists
      ? old.map((existing) => (existing.id === user.id ? user : existing))
      : [...old, user];
  });
}

/** Some mutation responses omit product_ids/product_names (role change,
 *  activate) or return no body at all (deactivate) — for those, pull the one
 *  row fresh via GET /admin/users/:id rather than guess at the delta or
 *  refetch the whole list. */
async function refreshUserInCache(queryClient: QueryClient, id: number) {
  const fresh = await portalApi.getUser(id);
  putUserInCache(queryClient, fresh);
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UserCreatePayload) => portalApi.createUser(body),
    // Refetch rather than append via putUserInCache: unlike the other
    // mutations below (which edit a row already in the cached list, so
    // splicing it back in place is safe), a new row's position depends on
    // however the backend orders /admin/users — appending to the end here
    // was landing new users on a different page than a fresh GET puts them
    // on, making them look missing until a manual reload.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PORTAL_KEYS.users });
      toast.success("User created");
    },
    onError: toastApiError("Could not create the user"),
  });
}

export function useUpdateUserProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UserProfileUpdatePayload }) =>
      portalApi.updateUserProfile(id, body),
    onSuccess: (updated) => {
      putUserInCache(queryClient, updated);
      toast.success("User updated");
    },
    onError: toastApiError("Could not update the user"),
  });
}

export function useUpdateUserRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role_id }: { id: number; role_id: number }) =>
      portalApi.updateUserRole(id, role_id),
    // Response omits product_ids/product_names — refresh just this row.
    onSuccess: async (_data, variables) => {
      await refreshUserInCache(queryClient, variables.id);
      toast.success("Role updated");
    },
    onError: toastApiError("Could not change the role"),
  });
}

/** Full replace of a manager's products. */
export function useUpdateUserProducts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, product_ids }: { id: number; product_ids: number[] }) =>
      portalApi.updateUserProducts(id, product_ids),
    onSuccess: (updated) => {
      putUserInCache(queryClient, updated);
      toast.success("Products updated");
    },
    onError: toastApiError("Could not update assigned products"),
  });
}

export function useMoveUserProgram() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, program_id }: { id: number; program_id: number }) =>
      portalApi.moveUserProgram(id, program_id),
    onSuccess: (updated) => {
      putUserInCache(queryClient, updated);
      toast.success("Program updated");
    },
    onError: toastApiError("Could not move the user"),
  });
}

export function useUpdateUserPassword() {
  return useMutation({
    mutationFn: ({ id, password }: { id: number; password: string }) =>
      portalApi.updateUserPassword(id, password),
    onSuccess: () => {
      toast.success("Password updated");
    },
    onError: toastApiError("Could not update the password"),
  });
}

export function useSetUserActive() {
  const queryClient = useQueryClient();
  return useMutation({
    // Return value is discarded either way (onSuccess refetches the row
    // fresh below) — activateUser/deactivateUser resolve to different types
    // (PortalUser vs void), so the mutationFn is typed uniformly as void.
    mutationFn: async ({ id, active }: { id: number; active: boolean }) => {
      if (active) await portalApi.activateUser(id);
      else await portalApi.deactivateUser(id);
    },
    // Activate's response is partial and deactivate (DELETE) returns no body
    // at all — refresh just this row from GET /admin/users/:id either way.
    onSuccess: async (_data, variables) => {
      await refreshUserInCache(queryClient, variables.id);
      toast.success(variables.active ? "User activated" : "User deactivated");
    },
    onError: toastApiError("Could not change user status"),
  });
}

/** Resets the account to the temporary password and resends the welcome
 *  email — the 200 body's `sent` still needs checking: a 200 means the
 *  request succeeded, not that the mail server accepted the message (no mail
 *  transport configured, refused address, etc. all come back `sent: false`
 *  rather than a 4xx/5xx, same as the original invite). */
export function useResendInvite() {
  return useMutation({
    mutationFn: (id: number) => portalApi.resendInvite(id),
    onSuccess: (data) => {
      if (data.sent) toast.success("Invite email resent");
      else
        toast.warning(
          "Password was reset, but the invite email could not be sent — check the mail configuration",
        );
    },
    onError: toastApiError("Could not resend the invite"),
  });
}

// ── Programs (companies) ──────────────────────────────────────────────────────

export const usePortalPrograms = (enabled = true) =>
  useQuery({
    queryKey: PORTAL_KEYS.programs,
    queryFn: portalApi.listPrograms,
    enabled,
  });

export const usePortalProducts = (enabled = true) =>
  useQuery({
    queryKey: PORTAL_KEYS.products,
    queryFn: portalApi.listProducts,
    enabled,
  });

export const usePortalTiers = () =>
  useQuery({
    queryKey: PORTAL_KEYS.tiers,
    queryFn: portalApi.listTiers,
    staleTime: Infinity,
  });

/** Tiers licensed by one program — fetched when its edit modal opens. */
export const useProgramTiers = (id: number, enabled = true) =>
  useQuery({
    queryKey: PORTAL_KEYS.programTiers(id),
    queryFn: () => portalApi.getProgramTiers(id),
    enabled,
  });

export function useCreateProgram() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ProgramCreatePayload) => portalApi.createProgram(body),
    onSuccess: () => {
      // Attaching products moves their ownership — refresh both catalogs.
      queryClient.invalidateQueries({ queryKey: PORTAL_KEYS.programs });
      queryClient.invalidateQueries({ queryKey: PORTAL_KEYS.products });
      toast.success("Program created");
    },
    onError: toastApiError("Could not create the program"),
  });
}

export function useUpdateProgram() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: ProgramUpdatePayload }) =>
      portalApi.updateProgram(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PORTAL_KEYS.programs });
      queryClient.invalidateQueries({ queryKey: PORTAL_KEYS.products });
      toast.success("Program updated");
    },
    onError: toastApiError("Could not update the program"),
  });
}

// ── Tiers ─────────────────────────────────────────────────────────────────────

export function useCreateTier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TierCreatePayload) => portalApi.createTier(body),
    onSuccess: (created) => {
      queryClient.setQueryData<PortalTier[]>(PORTAL_KEYS.tiers, (old) =>
        old ? [...old, created] : old,
      );
      toast.success("Tier created");
    },
    onError: toastApiError("Could not create the tier"),
  });
}

export function useUpdateTier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: TierUpdatePayload }) =>
      portalApi.updateTier(id, body),
    onSuccess: (updated) => {
      queryClient.setQueryData<PortalTier[]>(PORTAL_KEYS.tiers, (old) =>
        old?.map((tier) => (tier.id === updated.id ? updated : tier)),
      );
      toast.success("Tier updated");
    },
    onError: toastApiError("Could not update the tier"),
  });
}

// ── Products ──────────────────────────────────────────────────────────────────

export function useCreateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ProductCreatePayload) => portalApi.createProduct(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PORTAL_KEYS.products });
      toast.success("Product created");
    },
    onError: toastApiError("Could not create the product"),
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: ProductUpdatePayload }) =>
      portalApi.updateProduct(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PORTAL_KEYS.products });
      // Users embed product_names — a rename would leave their cache stale.
      queryClient.invalidateQueries({ queryKey: PORTAL_KEYS.users });
      toast.success("Product updated");
    },
    onError: toastApiError("Could not update the product"),
  });
}

export function useSetProgramTiers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tier_ids }: { id: number; tier_ids: number[] }) =>
      portalApi.setProgramTiers(id, tier_ids),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(PORTAL_KEYS.programTiers(variables.id), data);
      // The clients list embeds `tiers` per row (Tiers column on
      // ClientsPage) — keep it in sync or it'd show stale tiers until the
      // whole list refetches.
      queryClient.setQueryData<PortalProgram[]>(PORTAL_KEYS.programs, (old) =>
        old?.map((client) =>
          client.id === variables.id ? { ...client, tiers: data } : client,
        ),
      );
      toast.success("Licensed tiers updated");
    },
    onError: toastApiError("Could not update licensed tiers"),
  });
}

// ── Dashboards (template catalog) ────────────────────────────────────────────

export const usePortalDashboards = () =>
  useQuery({ queryKey: PORTAL_KEYS.dashboards, queryFn: portalApi.listDashboards });

/** Mutation responses here are all partial/differently-keyed — invalidate
 *  and let the list refetch rather than patch the cache by hand. */
export function useCreateDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DashboardCreatePayload) => portalApi.createDashboard(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PORTAL_KEYS.dashboards });
      toast.success("Dashboard created");
    },
    onError: toastApiError("Could not create the dashboard"),
  });
}

export function useUpdateDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: DashboardUpdatePayload }) =>
      portalApi.updateDashboard(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PORTAL_KEYS.dashboards });
      toast.success("Dashboard updated");
    },
    onError: toastApiError("Could not update the dashboard"),
  });
}

export function useSetDashboardTiers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: DashboardTiersAssignPayload }) =>
      portalApi.setDashboardTiers(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PORTAL_KEYS.dashboards });
      toast.success("Tiers updated");
    },
    onError: toastApiError("Could not update tiers"),
  });
}

/** Full replace of the clients hidden from a template dashboard. Invalidates
 *  the dashboard list because that is where `excluded_client_ids` lives, so
 *  the refetch is what keeps the modal in sync — no extra query key needed. */
export function useSetDashboardExcludedClients() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: number;
      body: DashboardExcludedClientsPayload;
    }) => portalApi.setDashboardExcludedClients(id, body),
    onSuccess: (data) => {
      // Patch the affected row from the PATCH's own authoritative echo. This
      // is what makes a reopened modal show the new checkboxes right away:
      // relying on invalidate alone left a window where the refetch hadn't
      // landed yet, and the modal seeds its checkbox state from whatever the
      // cached row said at that moment.
      queryClient.setQueryData<PortalDashboard[]>(
        PORTAL_KEYS.dashboards,
        (rows) =>
          rows?.map((row) =>
            row.id === data.dashboard_id
              ? { ...row, excluded_client_ids: data.excluded_client_ids }
              : row,
          ),
      );
      // Still invalidate, so anything else the save changed server-side
      // converges — the cache write above just means we are never showing
      // stale checkboxes while that request is in flight.
      queryClient.invalidateQueries({ queryKey: PORTAL_KEYS.dashboards });
      toast.success("Restricted clients updated");
    },
    onError: toastApiError("Could not update restricted clients"),
  });
}

/** Deactivate is DELETE (soft); reactivate is PATCH is_active=true — same
 *  toggle from the caller's side, mirroring useSetUserActive. */
export function useSetDashboardActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, active }: { id: number; active: boolean }) => {
      if (active) await portalApi.updateDashboard(id, { is_active: true });
      else await portalApi.deactivateDashboard(id);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: PORTAL_KEYS.dashboards });
      toast.success(
        variables.active ? "Dashboard activated" : "Dashboard deactivated",
      );
    },
    onError: toastApiError("Could not change dashboard status"),
  });
}

// ── Widget library (KPIs) ────────────────────────────────────────────────────

/** The KPI catalog. Was `staleTime: Infinity` — never refetched for the whole
 *  session — which is wrong for a list that moves underneath the admin: each
 *  widget carries the dashboards it sits on, and those change whenever anyone
 *  saves a layout in the builder or deactivates a widget here. A stale
 *  `dashboards` list is what the deactivation warning is built on, so it has
 *  to reflect the server, not a snapshot from when the tab was opened. */
export const usePortalWidgetTemplates = () =>
  useQuery({
    queryKey: PORTAL_KEYS.widgetTemplates,
    queryFn: portalApi.listWidgetTemplates,
    staleTime: 0,
  });

export function useCreateWidgetTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: WidgetTemplateCreatePayload) =>
      portalApi.createWidgetTemplate(body),
    onSuccess: (created) => {
      queryClient.setQueryData<PortalWidgetTemplate[]>(
        PORTAL_KEYS.widgetTemplates,
        (old) => (old ? [...old, created] : old),
      );
      toast.success("Widget created");
    },
    onError: toastApiError("Could not create the widget"),
  });
}

/** PATCH response is partial (no chart_type/notes/grid dims) — merge just
 *  the fields it returns into the cached row instead of replacing it. */
export function useUpdateWidgetTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: number;
      body: WidgetTemplateUpdatePayload;
    }) => portalApi.updateWidgetTemplate(id, body),
    onSuccess: (updated) => {
      queryClient.setQueryData<PortalWidgetTemplate[]>(
        PORTAL_KEYS.widgetTemplates,
        (old) =>
          old?.map((widget) =>
            widget.id === updated.id
              ? {
                  ...widget,
                  title: updated.title,
                  description: updated.description,
                  group: updated.group,
                  tier: updated.tier,
                  ...(updated.is_active !== undefined && {
                    is_active: updated.is_active,
                  }),
                }
              : widget,
          ),
      );
      toast.success("Widget updated");
    },
    onError: toastApiError("Could not update the widget"),
  });
}

/** Patches the row's is_active in place from the PATCH response, same
 *  pattern as useSetDashboardActive — a full refetch isn't needed since
 *  GET /widget_templates already returns inactive widgets too.
 *
 *  Deactivating also deletes the widget's placements on every dashboard, in
 *  the same server commit (confirmed in the endpoint's own contract), so the
 *  cached `dashboards` list is emptied here too — otherwise the row keeps
 *  claiming links the server has already dropped, and reactivating would show
 *  a warning for dashboards the widget is no longer on. Reactivating never
 *  restores them, so there is nothing to put back. */
export function useSetWidgetTemplateActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      portalApi.updateWidgetTemplate(id, { is_active: active }),
    onSuccess: (updated, variables) => {
      queryClient.setQueryData<PortalWidgetTemplate[]>(
        PORTAL_KEYS.widgetTemplates,
        (old) =>
          old?.map((widget) =>
            widget.id === updated.id
              ? {
                  ...widget,
                  is_active: updated.is_active ?? variables.active,
                  dashboards: variables.active ? widget.dashboards : [],
                }
              : widget,
          ),
      );
      toast.success(
        variables.active
          ? "Widget activated"
          : describeDeactivation(updated.removed_from),
      );
    },
    onError: toastApiError("Could not change widget status"),
  });
}

/** Toast copy for a deactivation, naming how many dashboards actually lost
 *  the widget. `removed_from` is absent on a backend that predates the
 *  cascade, so the plain message stays the fallback rather than claiming a
 *  removal that may not have happened. */
function describeDeactivation(
  removedFrom: WidgetTemplateUpdateResponse["removed_from"],
): string {
  if (!removedFrom) return "Widget deactivated";
  const total =
    removedFrom.admin_dashboards.length + removedFrom.user_dashboard_count;
  if (total === 0) return "Widget deactivated";
  return `Widget deactivated and removed from ${total} dashboard${
    total === 1 ? "" : "s"
  }`;
}
